import { useEffect, useRef, useState } from 'react';
import type {
  Referral,
  ReferralJobTitle,
  ReferralSettings,
  ReferralWorker,
  SaveReferralRequest,
} from '../../api/types';
import { EditIcon } from './RowIcons';
import { derivedDates, formatDate, submissionWindow } from './referralDates';

/** The select's escape hatch for a title the configuration does not have yet. */
const OTHER = '__other__';

/** The same escape hatch, for a referrer the payroll list does not have. */
const SOMEONE_ELSE = '__someone_else__';

interface Props {
  /** The referral being edited, or null to add a new one. */
  referral: Referral | null;
  jobTitles: ReferralJobTitle[];

  /**
   * The payroll roster the referrer is picked from. Empty is a working state — the list is never the
   * only way to name somebody, so an unreachable feed leaves the field usable rather than broken.
   */
  workers: ReferralWorker[];
  settings: ReferralSettings;

  /** The payroll calendar, for showing the payout date a hire date lands on. */
  payoutDates: readonly string[];

  /** Today as a local `yyyy-mm-dd`. The section builds it from local parts; see why there. */
  today: string;
  saving: boolean;

  /** Per-field messages from the API, keyed as the contracts name them. */
  fieldErrors: Record<string, string[]>;
  error: string | null;
  onSave: (request: SaveReferralRequest) => void;
  onClose: () => void;
}

interface FormState {
  referringEmployeeName: string;

  /** The referrer select's own value: a payroll id, `SOMEONE_ELSE`, or '' for nothing chosen. */
  referringEmployeeChoice: string;
  newHireName: string;
  jobTitle: string;
  jobTitleChoice: string;
  department: string;
  location: string;
  hireDate: string;
  payoutAmount: string;

  /** A payout date pinned by hand, or '' to keep following the schedule. */
  payoutDateOverride: string;
  isPaid: boolean;
  completed: boolean;
  notes: string;
}

function initialState(
  referral: Referral | null,
  jobTitles: ReferralJobTitle[],
  workers: ReferralWorker[],
): FormState {
  const known = referral !== null && jobTitles.some((t) => t.title === referral.jobTitle);

  // Matched on the name, because the name is all a referral stores. For the pair of active workers
  // who share one, the first is picked and nothing is lost — which of them it was has never been
  // recorded, so there is nothing here to recover.
  const referrer =
    referral === null
      ? undefined
      : workers.find((worker) => worker.name === referral.referringEmployeeName);

  return {
    referringEmployeeName: referral?.referringEmployeeName ?? '',

    // Anyone the payroll list does not have — somebody who has since left it, or a referral recorded
    // before the list existed — opens on the free-text branch rather than on an empty select.
    referringEmployeeChoice:
      referral === null ? '' : referrer !== undefined ? referrer.employeeId : SOMEONE_ELSE,
    newHireName: referral?.newHireName ?? '',
    jobTitle: referral?.jobTitle ?? '',
    jobTitleChoice: referral === null ? '' : known ? referral.jobTitle : OTHER,
    department: referral?.department ?? '',
    location: referral?.location ?? '',
    hireDate: referral?.hireDate ?? '',
    payoutAmount: referral === null ? '' : String(referral.payoutAmount),

    // Only a date that was actually pinned comes back as one. A referral following the schedule has a
    // payoutDate too, and seeding the box with it would turn every edit into a fresh override — the
    // row would stop moving when payroll moves the run.
    payoutDateOverride:
      referral !== null && referral.payoutDateIsOverridden ? referral.payoutDate ?? '' : '',
    isPaid: referral?.isPaid ?? false,
    completed: referral?.completed ?? false,
    notes: referral?.notes ?? '',
  };
}

/**
 * The same rules the API applies, checked here first so a missing value is answered under the field
 * that is missing rather than by a round trip that comes back as a banner. The API checks them again —
 * this copy is for the message, not for the guarantee.
 *
 * `window` is null when editing, because the API applies the submission window to a create and not to
 * an edit. A form that refused an edit would make every referral older than the window impossible to
 * correct.
 */
function validate(
  form: FormState,
  window: { earliestHireDate: string; days: number } | null,
): Record<string, string[]> {
  const errors: Record<string, string[]> = {};

  if (form.referringEmployeeName.trim() === '') errors.referringEmployeeName = ['Who made the referral?'];
  if (form.newHireName.trim() === '') errors.newHireName = ['Who was referred?'];
  if (form.jobTitle.trim() === '') errors.jobTitle = ['What were they hired as?'];
  if (form.hireDate === '') {
    errors.hireDate = ['A hire date is needed — the 90-day and payout dates are worked out from it.'];
  } else {
    const outsideWindow = hireDateWindowError(form.hireDate, window);

    if (outsideWindow !== undefined) errors.hireDate = [outsideWindow];
  }

  return errors;
}

/**
 * The submission window's complaint about a hire date, or undefined when it has none.
 *
 * Its own function because it is asked twice: once by {@link validate} on submit, which is what stops
 * the request, and once during render, which is what puts the message on screen the moment a date is
 * picked. One spelling of the rule and one spelling of the sentence, so the two cannot drift.
 */
function hireDateWindowError(
  hireDate: string,
  window: { earliestHireDate: string; days: number } | null,
): string | undefined {
  // Plain string comparison, for the same reason as everywhere else in this section.
  if (window === null || hireDate === '' || hireDate >= window.earliestHireDate) return undefined;

  return `That hire date is more than ${window.days} days ago. A referral has to be recorded within `
    + `${window.days} days of the new hire starting.`;
}

/**
 * Add and edit are one dialog because they collect the same thing — but not quite the same set of
 * answers. A new referral is always for an active hire, undecided, unpaid and unfinished, so none of
 * those is asked when adding: they are answers someone gives months later, and a form that asks for
 * them on day one invites a wrong one that nobody revisits.
 *
 * Eligibility is not in here at all, even on an edit. It is one decision made from the table, next to
 * the rows that are waiting for it.
 *
 * A native `<dialog>` rather than a div with an overlay: it traps focus, closes on Escape and sits in
 * the top layer without this section having to reimplement any of that.
 */
export function ReferralFormDialog(props: Props) {
  const {
    referral, jobTitles, workers, settings, payoutDates, today, saving, fieldErrors, error, onSave, onClose,
  } = props;

  const isEdit = referral !== null;
  const dialogRef = useRef<HTMLDialogElement>(null);
  const [form, setForm] = useState<FormState>(() => initialState(referral, jobTitles, workers));
  const [localErrors, setLocalErrors] = useState<Record<string, string[]>>({});

  useEffect(() => {
    dialogRef.current?.showModal();
  }, []);

  const set = <K extends keyof FormState>(key: K, value: FormState[K]) =>
    setForm((current) => ({ ...current, [key]: value }));

  /**
   * Changing the job title **always** rewrites the payout, and clearing it empties the payout. The
   * amount belongs to the title that was chosen; leaving a previous title's amount sitting under a new
   * one is how a Chiropractic Technician silently gets a Chiropractor's $1,000.
   *
   * The person entering it can still type over the amount afterwards — the configured value is where
   * the box starts, not where it has to stay.
   */
  /**
   * The name is what gets saved, so it is written from the roster rather than from what the select
   * happens to display — the option's value is a payroll id, and two of those carry the same name.
   */
  function chooseReferrer(choice: string) {
    const worker = workers.find((w) => w.employeeId === choice);

    setForm((current) => ({
      ...current,
      referringEmployeeChoice: choice,
      referringEmployeeName: worker?.name ?? '',
    }));
  }

  function chooseJobTitle(choice: string) {
    const configured = jobTitles.find((t) => t.title === choice);

    setForm((current) => ({
      ...current,
      jobTitleChoice: choice,
      jobTitle: choice === OTHER ? '' : choice,
      payoutAmount: configured === undefined ? '' : String(configured.defaultPayoutAmount),
    }));
  }

  // Only when adding. The window is a rule about recording a referral, not about the referral.
  const window = isEdit ? null : submissionWindow(today, settings);

  /** The window's complaint about the hire date as it stands, or undefined when it has none. */
  const hireDateOutsideWindow = hireDateWindowError(form.hireDate, window);

  /**
   * The two derived dates, or null when there is no hire date to work them out from — **or when the
   * hire date is one this form is already refusing**. Dates derived from a value that will not be
   * accepted answer a question nobody asked, and a payout date sitting under "that hire date is more
   * than 14 days ago" states two things that cannot both be true of the same referral.
   */
  const preview = hireDateOutsideWindow === undefined
    ? derivedDates(form.hireDate, settings, payoutDates)
    : null;

  /** What the calendar says, before anybody pins anything. Null when it does not reach that far. */
  const scheduledPayoutDate = preview?.payoutDate ?? null;

  /** What this referral would actually be paid on — the pinned date if there is one. */
  const payoutDate = form.payoutDateOverride === '' ? scheduledPayoutDate : form.payoutDateOverride;

  /** Opened by the pencil. Nothing opens it on the form's own initiative. */
  const [payoutPickerOpen, setPayoutPickerOpen] = useState(false);

  /**
   * Runs still to come — a payout date is being moved because the scheduled one will not do, and
   * moving it to a day already gone by is not something this form has any use for.
   *
   * With one exception, kept so the control cannot misreport: a referral already pinned to a past run
   * — which is what the missed-payout flow does — keeps that date in the list. Left out, the select
   * would fall back to its first option and quietly show a date the referral does not have.
   *
   * Plain string comparison on `yyyy-mm-dd`, as everywhere else in this section.
   */
  const selectableRuns = payoutDates.filter(
    (date) => date >= today || date === form.payoutDateOverride,
  );

  function submit(event: React.FormEvent) {
    event.preventDefault();

    const errors = validate(form, window);
    setLocalErrors(errors);

    if (Object.keys(errors).length > 0) return;

    onSave({
      referringEmployeeName: form.referringEmployeeName.trim(),
      newHireName: form.newHireName.trim(),
      jobTitle: form.jobTitle.trim(),
      department: form.department.trim() || null,
      location: form.location.trim() || null,

      // Null rather than "": an empty string is not a date, and the JSON binder rejects the whole
      // body before this module's validation — which can name the field — ever runs.
      hireDate: form.hireDate || null,

      // Only sent on an edit. A new referral is always undecided, unpaid and unfinished, and the API
      // sets those itself rather than trusting whatever a request claims about them.
      ...(isEdit ? { isPaid: form.isPaid, completed: form.completed } : {}),

      // '' is "keep following the schedule", which has to travel as null — the difference between
      // this row's date being pinned and it moving when payroll moves the run.
      payoutDateOverride: form.payoutDateOverride === '' ? null : form.payoutDateOverride,

      // Left null when nothing was typed, so the API fills it from the job title's configured amount
      // rather than this form guessing at one.
      payoutAmount: form.payoutAmount.trim() === '' ? null : Number(form.payoutAmount),
      notes: form.notes.trim() || null,
    });
  }

  // The local check runs first, so a field it caught shows its own message rather than the API's.
  const errorFor = (key: string) => localErrors[key]?.[0] ?? fieldErrors[key]?.[0];

  return (
    <dialog className="dialog" ref={dialogRef} onCancel={onClose} onClose={onClose}>
      <form className="dialog__form" onSubmit={submit} noValidate>
        <h2 className="dialog__title">{isEdit ? 'Edit referral' : 'Add a referral'}</h2>

        {/* Only for something no field can explain. Anything about a particular value belongs under
            that value, which is why the required-field check above runs before the request is sent. */}
        {error !== null && <p className="dialog__error">{error}</p>}

        <div className="dialog__grid">
          {/* The same control as the job title below it, down to the "…else" escape hatch, because
              they are the same kind of question and a form that answers two of them two different
              ways makes the reader work out why. A datalist was tried here first: it cannot be styled
              to match a select — the browser draws it — and picking from it is unreliable. */}
          <Field label="Team member" required error={errorFor('referringEmployeeName')}>
            <select
              className={controlClass(errorFor('referringEmployeeName'))}
              value={form.referringEmployeeChoice}
              onChange={(e) => chooseReferrer(e.target.value)}
              autoFocus
            >
              <option value="">Choose a team member…</option>
              {workers.map((worker) => (
                // Valued and keyed on the payroll id rather than the name, because two active workers
                // share a name today and two options with one value cannot be told apart. The job
                // title in the label is what tells that pair apart on screen.
                <option key={worker.employeeId} value={worker.employeeId}>
                  {worker.jobTitle === null ? worker.name : `${worker.name} — ${worker.jobTitle}`}
                </option>
              ))}
              <option value={SOMEONE_ELSE}>Someone else…</option>
            </select>

            {/* Not a fallback for an empty roster — a referrer who has since left the payroll is not
                on it, and neither is anyone named on a referral recorded before it existed. */}
            {form.referringEmployeeChoice === SOMEONE_ELSE && (
              <input
                className="control dialog__other"
                value={form.referringEmployeeName}
                onChange={(e) => set('referringEmployeeName', e.target.value)}
                placeholder="Type the name"
              />
            )}
          </Field>

          <Field label="New hire" required error={errorFor('newHireName')}>
            <input
              className={controlClass(errorFor('newHireName'))}
              value={form.newHireName}
              onChange={(e) => set('newHireName', e.target.value)}
              placeholder="Who was referred"
            />
          </Field>

          <Field label="Job title" required error={errorFor('jobTitle')}>
            <select
              className={controlClass(errorFor('jobTitle'))}
              value={form.jobTitleChoice}
              onChange={(e) => chooseJobTitle(e.target.value)}
            >
              <option value="">Choose a job title…</option>
              {jobTitles
                .filter((t) => t.isActive || t.title === form.jobTitle)
                .map((t) => (
                  <option key={t.id} value={t.title}>
                    {t.title}
                  </option>
                ))}
              <option value={OTHER}>Something else…</option>
            </select>

            {form.jobTitleChoice === OTHER && (
              <input
                className="control dialog__other"
                value={form.jobTitle}
                onChange={(e) => set('jobTitle', e.target.value)}
                placeholder="Type the job title"
              />
            )}
          </Field>

          <Field label="Payout" error={errorFor('payoutAmount')}>
            <MoneyInput
              value={form.payoutAmount}
              invalid={errorFor('payoutAmount') !== undefined}
              onChange={(value) => set('payoutAmount', value)}
            />
          </Field>

          <Field label="Department" error={errorFor('department')}>
            <input
              className={controlClass(errorFor('department'))}
              value={form.department}
              onChange={(e) => set('department', e.target.value)}
            />
          </Field>

          <Field label="Location" error={errorFor('location')}>
            <input
              className={controlClass(errorFor('location'))}
              value={form.location}
              onChange={(e) => set('location', e.target.value)}
              placeholder="e.g. Springfield"
            />
          </Field>

          {/* No `min` on the input: the form is noValidate, so the constraint would not block submit,
              and the browser's own bubble would bypass the under-the-field message this section is
              built around. */}
          {/* Answered as soon as a date is picked, not held back until the submit button. A date
              outside the window is settled the moment it is chosen — unlike a name still being typed
              — so saying nothing leaves someone filling in the rest of a form that was never going to
              be accepted. Submit checks it again; this is the message, not the guarantee. */}
          <Field
            label="Hire date"
            required
            help={
              window === null
                ? undefined
                : `Ahead of today is fine. A start date already past, only within the last ${window.days} days.`
            }
            error={hireDateOutsideWindow ?? errorFor('hireDate')}
          >
            <input
              className={controlClass(hireDateOutsideWindow ?? errorFor('hireDate'))}
              type="date"
              value={form.hireDate}
              onChange={(e) => set('hireDate', e.target.value)}
            />
          </Field>

          {/* Both dates follow from the hire date, so neither is a field. Shown as soon as there is a
              hire date to work them out from, because "when does this fall due" is the question being
              asked while the row is typed in. */}
          {preview !== null && (
            <div className="dialog__derived">
              <p className="dialog__derived-line">
                <strong>{settings.probationDays} days</strong> agreement on{' '}
                {formatDate(preview.probationClearedOn)} ·{' '}
                {payoutDate === null ? (
                  <>
                    potential payout date <span className="field__warn">not in the calendar yet</span>
                  </>
                ) : (
                  <>
                    {form.payoutDateOverride === '' ? 'potential payout date ' : 'payout date '}
                    <strong>{formatDate(payoutDate)}</strong>
                    {form.payoutDateOverride !== '' && (
                      <span className="dialog__derived-tag">set by hand</span>
                    )}
                  </>
                )}

                {/* Hidden while the picker is open — the control it opens is already on screen, and
                    a button that does nothing is worse than no button. */}
                {!payoutPickerOpen && (
                  <button
                    type="button"
                    className="icon-button icon-button--inline"
                    title="Choose a different payout date"
                    aria-label="Choose a different payout date"
                    onClick={() => setPayoutPickerOpen(true)}
                  >
                    <EditIcon />
                  </button>
                )}
              </p>

              {payoutPickerOpen && (
                <label className="dialog__derived-picker">
                  <span>Payout date</span>
                  <select
                    className="control"
                    value={form.payoutDateOverride}
                    onChange={(e) => set('payoutDateOverride', e.target.value)}
                  >
                    {/* The way back. Without it a date pinned by mistake could never be unpinned, and
                        the row would stop following payroll for good. */}
                    <option value="">
                      {scheduledPayoutDate === null
                        ? 'Follow the schedule'
                        : `Follow the schedule — ${formatDate(scheduledPayoutDate)}`}
                    </option>

                    {selectableRuns.map((date) => (
                      <option key={date} value={date}>
                        {formatDate(date)}
                      </option>
                    ))}
                  </select>
                </label>
              )}
            </div>
          )}

          {isEdit && (
            <Field label="Paid and completed" wide>
              <div className="checkbox-row">
                <label className="checkbox">
                  <input
                    type="checkbox"
                    checked={form.isPaid}
                    onChange={(e) => set('isPaid', e.target.checked)}
                  />
                  <span>The money went out</span>
                </label>

                <label className="checkbox">
                  <input
                    type="checkbox"
                    checked={form.completed}
                    onChange={(e) => set('completed', e.target.checked)}
                  />
                  <span>Finished — nothing further to do</span>
                </label>
              </div>
            </Field>
          )}

          <Field label="Notes" wide error={errorFor('notes')}>
            <textarea
              className="control"
              rows={3}
              value={form.notes}
              onChange={(e) => set('notes', e.target.value)}
              placeholder="Why it is not eligible, or anything else worth recording"
            />
          </Field>
        </div>

        <div className="dialog__actions">
          <button type="button" className="button button--quiet" onClick={onClose} disabled={saving}>
            Cancel
          </button>
          <button type="submit" className="button button--primary" disabled={saving}>
            {saving ? 'Saving…' : isEdit ? 'Save changes' : 'Add referral'}
          </button>
        </div>
      </form>
    </dialog>
  );
}

function controlClass(error: string | undefined): string {
  return error === undefined ? 'control' : 'control control--invalid';
}

/**
 * A number field that reads as money. The `$` is drawn inside the field's box but is not part of its
 * value, so what gets typed and submitted stays a plain number.
 *
 * No placeholder amount: a greyed-out "500" in an empty box is read as a value that is already there,
 * and this box is empty until a job title supplies one.
 */
export function MoneyInput(props: {
  value: string;
  invalid?: boolean;
  onChange: (value: string) => void;
}) {
  return (
    <div className={props.invalid === true ? 'money money--invalid' : 'money'}>
      <span className="money__symbol" aria-hidden="true">
        $
      </span>
      <input
        className="money__input"
        type="number"
        inputMode="decimal"
        min="0"
        step="50"
        value={props.value}
        onChange={(e) => props.onChange(e.target.value)}
      />
    </div>
  );
}

function Field(props: {
  label: string;
  children: React.ReactNode;
  required?: boolean;
  wide?: boolean;
  help?: string;
  error?: string;
}) {
  return (
    <div className={props.wide === true ? 'field field--wide' : 'field'}>
      <label className="field__label">
        {props.label}
        {props.required === true && <span className="field__required">*</span>}
      </label>
      {props.children}
      {props.error === undefined
        ? props.help !== undefined && <p className="field__help">{props.help}</p>
        : <p className="field__error">{props.error}</p>}
    </div>
  );
}
